import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { MaskedLine, FadeUp, EASE } from "@/components/Reveal";
import RealisticIPhone from "@/components/RealisticIPhone";
import { GALLERY } from "@/data/deviceScreens";
import { apps } from "@/data/products";

const CYCLE = ["icd10-on-the-go-2026", "icd10-medical-codes", "icd10-2026", "medical-abbreviations", "clinical-coding-copilot", "acroly"];

export default function Hero() {
  const { t, localizePath } = useLang();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % CYCLE.length), 5200);
    return () => clearInterval(id);
  }, [reduce]);

  const lines = t("hero.lines");

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div className="mx-auto grid min-h-screen w-full max-w-[1400px] grid-cols-1 items-center gap-10 px-5 pb-16 pt-28 md:grid-cols-12 md:px-10 md:pt-20">
        <div className="md:col-span-7">
          <FadeUp immediate delay={0.05}>
            <p className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.3em] text-ink/50">
              <span className="inline-block h-px w-8 bg-cobalt" aria-hidden="true" />
              {t("hero.eyebrow")}
            </p>
          </FadeUp>

          <h1 className="mt-8 font-serif text-[15vw] font-medium leading-[0.98] tracking-[-0.01em] text-ink sm:text-7xl lg:text-[5.6rem] xl:text-[6.5rem]">
            {lines.map((line, i) => (
              <MaskedLine key={line} delay={0.05 + i * 0.09}>
                {i === 2 ? (
                  <>
                    <em className="italic">{line.split(" ")[0]}</em>{" "}
                    {line.split(" ").slice(1).join(" ")}
                  </>
                ) : (
                  line
                )}
              </MaskedLine>
            ))}
          </h1>

          <FadeUp immediate delay={0.35} className="mt-8 max-w-md">
            <p className="text-base leading-relaxed text-ink/60 md:text-lg">{t("hero.sub")}</p>
          </FadeUp>

          <FadeUp immediate delay={0.45} className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to={localizePath("/#products")}
              data-testid="hero-cta-products"
              className="group inline-flex items-center gap-2 rounded-full bg-cobalt px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-ink active:scale-[0.98]"
            >
              {t("hero.cta1")}
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </Link>
            <Link
              to={localizePath("/enterprise")}
              data-testid="hero-cta-enterprise"
              className="group inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-[border-color,color] duration-300 hover:border-cobalt hover:text-cobalt"
            >
              {t("hero.cta2")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeUp>
        </div>

        <div className="relative md:col-span-5">
          <FadeUp immediate delay={0.2} amount={0.1} className="relative">
            <motion.div
              style={reduce ? {} : { y: photoY }}
              className="relative mx-auto w-[88%] overflow-hidden border border-ink/10"
            >
              <img
                src="/themes/hero-flagship.webp"
                alt="A physician's desk with a reference book, stethoscope, and a cobalt blue pen"
                data-testid="hero-theme-image"
                className="h-[300px] w-full object-cover sm:h-[440px] lg:h-[520px]"
                loading="eager"
              />
            </motion.div>
            <motion.div
              animate={reduce ? {} : { y: [0, -8, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-14 left-1/2 -translate-x-1/2 [zoom:0.34] sm:-bottom-16 sm:-translate-x-[42%] sm:[zoom:0.38] lg:[zoom:0.42]"
            >
              <RealisticIPhone angle>
                <div className="absolute inset-0" data-testid="hero-device-screen">
                  {CYCLE.map((slug, i) => (
                    <motion.div
                      key={slug}
                      className="absolute inset-0"
                      initial={false}
                      animate={{ opacity: i === idx ? 1 : 0 }}
                      transition={{ duration: reduce ? 0 : 0.7, ease: EASE }}
                    >
                      {apps.find((app) => app.slug === slug)?.version ? (
                        <img src={apps.find((app) => app.slug === slug).screenshots[0]} alt={`${apps.find((app) => app.slug === slug).name} screenshot`} className="h-full w-full object-cover object-top" />
                      ) : GALLERY[slug][0](i === idx)}
                    </motion.div>
                  ))}
                </div>
              </RealisticIPhone>
            </motion.div>
          </FadeUp>
          <FadeUp immediate delay={0.4} className="relative mt-20 flex justify-center">
            <p className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-ink/40">
              <span className="h-px w-6 bg-ink/15" aria-hidden="true" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={CYCLE[idx]}
                  data-testid="hero-device-caption"
                  initial={reduce ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduce ? {} : { opacity: 0, y: -6 }}
                  transition={{ duration: 0.4, ease: EASE }}
                >
                  {apps.find((app) => app.slug === CYCLE[idx])?.name}
                </motion.span>
              </AnimatePresence>
              <span className="h-px w-6 bg-ink/15" aria-hidden="true" />
            </p>
          </FadeUp>
        </div>
      </div>

      <motion.div
        data-testid="scroll-indicator"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.0, duration: 0.6, ease: EASE }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-ink/40">{t("hero.scroll")}</span>
        <div className="h-10 w-px overflow-hidden bg-ink/10">
          <motion.div
            className="h-1/2 w-full bg-cobalt"
            animate={reduce ? {} : { y: ["-100%", "220%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
